import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1531591022136-eb8b0da1e6d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmUlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzg4MDUzMTg0fDA&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "We craft websites", accent: false },
  { text: "that turn visitors", accent: false },
  { text: "into revenue.", accent: true },
];

export const Hero = () => {
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrapRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);

  return (
    <section className="relative pt-36 md:pt-44" data-testid="hero-section">
      <motion.div
        className="fixed inset-0 z-50 bg-primary pointer-events-none"
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
        style={{ transformOrigin: "top" }}
        data-testid="hero-intro-curtain"
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-muted-foreground font-medium mb-8"
          data-testid="hero-kicker"
        >
          Sivic Business Design — Design studio for ambitious companies
        </motion.p>
        <h1
          className="font-syne font-bold tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
          data-testid="hero-headline"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-primary" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.55 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="max-w-md text-base md:text-lg text-muted-foreground font-light leading-relaxed"
            data-testid="hero-subcopy"
          >
            We design logos, websites, flyers, and brochures for businesses
            that refuse to look ordinary.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="flex items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-start"
              className="inline-flex items-center gap-2 bg-primary text-white px-7 py-4 text-sm font-medium hover:bg-foreground transition-colors duration-300 active:scale-[0.98]"
            >
              Start a project <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </div>
      <div ref={imgWrapRef} className="max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden h-[42vh] md:h-[68vh]"
          data-testid="hero-image-frame"
        >
          <motion.img
            src={HERO_IMG}
            alt="Minimal corporate architecture"
            style={{ y: parallaxY }}
            className="w-full h-full object-cover scale-[1.25]"
            data-testid="hero-image"
          />
        </motion.div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-4 mb-20 md:mb-28">
          Fig. 01 — Structure is beauty. The same is true of a website.
        </p>
      </div>
    </section>
  );
};
